/**
 * AuctionsV2Page — Phase 7 (additive, parallel to legacy /auctions).
 * Public marketplace of platform auctions.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Gavel, TrendingUp } from "lucide-react";

import { useI18n } from "../../lib/i18n";
import {
  auctionsAPI, AUCTION_STATUS_LABELS, formatCountdown,
} from "../../lib/auctionsV2";

const TABS = [
  { key: "live",      ar: "مباشر",    en: "Live"       },
  { key: "scheduled", ar: "قادم",     en: "Scheduled"  },
  { key: "sold",      ar: "منتهي",    en: "Ended"      },
];

export default function AuctionsV2Page() {
  const { lang } = useI18n();
  const [status, setStatus] = useState("live");
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    auctionsAPI.list({ status, limit: 30 })
      .then(setData)
      .catch(() => setData({ total: 0, items: [] }))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Refresh every 20s to catch new bids
  useEffect(() => {
    const t = setInterval(() => {
      auctionsAPI.list({ status, limit: 30 }).then(setData).catch(() => {});
    }, 20000);
    return () => clearInterval(t);
  }, [status]);

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#faf9f5]" dir={lang === "ar" ? "rtl" : "ltr"}
         data-testid="auctions-v2-page">
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg,#0a0a0a,#1f1614)" }}>
        <div className="max-w-6xl mx-auto px-6 py-10 sm:py-14">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#c9a55c] font-bold mb-2">
            <Gavel className="inline w-3.5 h-3.5 ms-1" /> CARTAL AUCTIONS
          </div>
          <h1 className="text-3xl sm:text-5xl font-display font-bold text-[#f7eecd] mb-2">
            {lang === "ar" ? "مزادات السيارات المباشرة" : "Live Vehicle Auctions"}
          </h1>
          <p className="text-[13px] text-[#f7eecd]/70 max-w-lg">
            {lang === "ar"
              ? "زايد على أفخم السيارات مع نظام حماية Escrow متكامل ومزايدة تلقائية ذكية."
              : "Bid on premium vehicles with full escrow protection and smart auto-bidding."}
          </p>
          <div className="mt-6 flex gap-2">
            {TABS.map((t) => (
              <button type="button" key={t.key} onClick={() => setStatus(t.key)}
                      data-testid={`auc-tab-${t.key}`}
                      className={`px-4 py-2 rounded-full text-[12px] font-bold transition-colors ${
                        status === t.key
                          ? "bg-[#c9a55c] text-[#0a0a0a]"
                          : "bg-white/[0.08] text-[#f7eecd]/80 hover:bg-white/[0.15]"
                      }`}>
                {lang === "ar" ? t.ar : t.en}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <div className="p-10 text-center text-sm text-[#8a8a8a]">…</div>
        ) : data.items.length === 0 ? (
          <div className="p-16 text-center rounded-2xl bg-white border border-dashed border-[#0a0a0a]/10"
               data-testid="auc-empty">
            <Gavel className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <div className="text-sm text-[#8a8a8a]">
              {lang === "ar" ? "لا توجد مزادات حالياً في هذه القائمة" : "No auctions in this list"}
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.items.map((a) => (
              <AuctionCard key={a.id} a={a} lang={lang} tick={tick} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AuctionCard({ a, lang, tick }) {
  const label = AUCTION_STATUS_LABELS[a.status] || { ar: a.status, en: a.status };
  const isLive = a.status === "live" || a.status === "extended";
  return (
    <Link
      to={`/auctions-v2/${a.id}`}
      className="block rounded-2xl bg-white border border-[#0a0a0a]/[0.06] overflow-hidden hover:shadow-lg hover:border-[#c9a55c]/50 transition-all group"
      data-testid={`auc-card-${a.id}`}
    >
      <div className="relative aspect-[4/3] bg-[#f0eee7]">
        {a.images?.[0] ? (
          <img src={a.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Gavel className="w-10 h-10 opacity-20" />
          </div>
        )}
        {isLive && (
          <span className="absolute top-3 start-3 px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-red-500 animate-pulse">
            {lang === "ar" ? "مباشر" : "LIVE"}
          </span>
        )}
        <span className="absolute top-3 end-3 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#0a0a0a] text-[#f7eecd]">
          {lang === "ar" ? label.ar : label.en}
        </span>
      </div>
      <div className="p-4">
        <div className="text-[13px] font-bold text-[#0a0a0a] truncate mb-1">{a.title_ar || a.title_en}</div>
        <div className="flex items-center justify-between text-[12px] mb-2">
          <span className="text-[#8a6f2e] font-semibold">
            {(a.current_price_sar || 0).toLocaleString()} {lang === "ar" ? "ر.س" : "SAR"}
          </span>
          <span className="text-[#525252] inline-flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> {a.bid_count} {lang === "ar" ? "مزايدة" : "bids"}
          </span>
        </div>
        {isLive && (
          <div className="text-[11px] text-red-600 font-bold inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {formatCountdown(a.ends_at)}
          </div>
        )}
      </div>
    </Link>
  );
}
